/**
 * Confirm your email (/verify-email).
 *
 * **This screen changed shape with the migration.** It used to collect a 6-digit
 * OTP that the API minted into Redis and emailed through Resend, then pushed
 * `emailVerified` into the identity provider so the token claim agreed. Better
 * Auth sends a verification LINK instead, so there is no code to type: the user
 * clicks the link, Better Auth marks the session verified, and they land back in
 * the app.
 *
 * What remains is a holding screen with a resend, and a poll so that clicking the
 * link in another tab moves this one along without a manual refresh.
 *
 * Sending is deliberately NOT done from an effect on mount: that would re-email on
 * every reload. The verification mail is dispatched by sign-up itself.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ds';
import { AUTH_BUTTON_STYLE, AuthError, AuthHeading, AuthLayout } from '@/components/auth/AuthLayout';
import { landingPathForAccount } from '@/lib/auth';
import { resendVerification } from '@/lib/session';
import { useAccount, useSession } from '@/hooks/useSession';

export default function VerifyEmailPage() {
  const { data: session } = useSession();
  const account = useAccount(Boolean(session?.user));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const email = session?.user.email ?? '';
  const verified = session?.user.emailVerified ?? false;

  // The link is usually opened in a different tab. Re-check periodically so this
  // tab follows along instead of stranding the user on a screen they have already
  // satisfied.
  useEffect(() => {
    if (verified) return;
    const id = setInterval(() => void account.refetch(), 5_000);
    return () => clearInterval(id);
  }, [verified, account]);

  const resend = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await resendVerification(email);
      setInfo('A new link is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the link.');
    } finally {
      setBusy(false);
    }
  };

  // Reached by a bookmark or the back button after confirming. The route guard
  // cannot catch this one: it lets any signed-in user in, precisely so an
  // unverified one is not bounced away from the page that fixes that.
  if (verified) return <Navigate to={landingPathForAccount(account.data)} replace />;

  return (
    <AuthLayout>
      <div className="flex flex-col gap-5">
        <AuthHeading title="Confirm your email">
          We&rsquo;ve sent a confirmation link to {email || 'your email'}. Open it to finish setting
          up your account.
        </AuthHeading>

        <AuthError>{error}</AuthError>
        {info && !error && <p className="text-sm text-forest">{info}</p>}

        <p className="text-sm text-ink-soft">
          You can leave this page open — it will move on by itself once you&rsquo;ve confirmed.
        </p>

        <Button
          type="button"
          style={AUTH_BUTTON_STYLE}
          disabled={busy}
          onClick={() => void resend()}
        >
          {busy ? 'Sending…' : 'Resend the link'}
        </Button>
      </div>
    </AuthLayout>
  );
}
